import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubUserSearchableMultiSelect } from "@/components/settings/HubUserSearchableMultiSelect";
import { apiFetch, ApiError } from "@/lib/api";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { unorderedStringArraysEqual } from "@/lib/settingsCardDirty";
import { formatTimestamp } from "@/lib/utils";

import {
  fetchInventoryDiscrepancySettings,
  INVENTORY_DISCREPANCY_SETTINGS_QUERY_KEY,
  patchInventoryDiscrepancySettings,
} from "./inventoryDiscrepancyApi";

interface UserOption {
  userId: string;
  email: string;
  displayName: string | null;
}

const SECTION_HELP =
  "Email addresses for hub users in this distribution group receive a notification when Picking submits a new SGS inventory discrepancy.";

export function InventoryDiscrepancyDistributionSection(): JSX.Element {
  const queryClient = useQueryClient();
  const canEdit = useHubModuleAdmin("manufacturing_management");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery({
    queryKey: INVENTORY_DISCREPANCY_SETTINGS_QUERY_KEY,
    queryFn: fetchInventoryDiscrepancySettings,
  });

  const usersQuery = useQuery({
    queryKey: ["manufacturing", "production-capacity", "staff-users"],
    queryFn: () =>
      apiFetch<{ users: UserOption[] }>("/api/manufacturing/production-capacity/staff/user-options"),
  });

  useEffect(() => {
    if (settingsQuery.data != null && !initialized) {
      setSelectedUserIds(settingsQuery.data.notifyUserIds);
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => patchInventoryDiscrepancySettings(selectedUserIds),
    onSuccess: (data) => {
      queryClient.setQueryData(INVENTORY_DISCREPANCY_SETTINGS_QUERY_KEY, data);
      setSelectedUserIds(data.notifyUserIds);
      setInitialized(true);
    },
  });

  const users = usersQuery.data?.users ?? [];
  const updatedAt = settingsQuery.data?.updatedAt ?? null;

  const persistedUserIds = useMemo(
    () => settingsQuery.data?.notifyUserIds ?? [],
    [settingsQuery.data?.notifyUserIds],
  );

  const isDirty = useMemo(() => {
    if (!initialized || settingsQuery.data == null) {
      return false;
    }
    return !unorderedStringArraysEqual(selectedUserIds, persistedUserIds);
  }, [initialized, persistedUserIds, selectedUserIds, settingsQuery.data]);

  return (
    <HubSettingsSection title="Inventory discrepancy notifications" hoverHint={SECTION_HELP}>
      {settingsQuery.isLoading || usersQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading hub users…</p>
      ) : settingsQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {settingsQuery.error instanceof ApiError
            ? settingsQuery.error.message
            : "Could not load distribution group."}
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm text-slate-600">No hub users available for selection.</p>
      ) : (
        <HubUserSearchableMultiSelect
          id="inventory-discrepancy-distribution"
          users={users}
          selectedUserIds={selectedUserIds}
          onSelectedUserIdsChange={setSelectedUserIds}
          disabled={!canEdit}
          placeholder="Select recipients…"
          noneSelectedLabel="No recipients"
        />
      )}

      <HubSettingsFooter>
        <div className="space-y-2">
          {saveMutation.error instanceof ApiError ? (
            <p className="text-sm text-rose-700" role="alert">
              {saveMutation.error.message}
            </p>
          ) : null}
          <HubSaveButton
            type="button"
            dirty={isDirty}
            disabled={!canEdit}
            blockedHint="Manufacturing management administrators can edit this distribution group."
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save distribution group
          </HubSaveButton>
        </div>
      </HubSettingsFooter>

      {updatedAt != null ? (
        <p className="text-xs text-slate-500">Last saved {formatTimestamp(updatedAt)}.</p>
      ) : null}
    </HubSettingsSection>
  );
}
